import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, getAuthToken } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/constants';
import { ArrowLeftIcon, EyeIcon } from '@heroicons/react/24/outline';
import RichTextEditor from '../../components/admin/RichTextEditor';
import AIWritingAssistant from '../../components/admin/AIWritingAssistant';
import AIContentButtons from '../../components/admin/AIContentButtons';
import AIGenerateButton from '../../components/admin/AIGenerateButton';
import AITagSuggestions from '../../components/admin/AITagSuggestions';
import AIKeywordSuggestButton from '../../components/admin/AIKeywordSuggestButton';
import PostPreview from '../../components/admin/PostPreview';

interface Category {
  id: string;
  name: string;
  slug: string;
  color: string;
}

interface Tag {
  id: string;
  name: string;
  slug: string;
  color: string;
}

interface PostFormData {
  title: string;
  content: string;
  excerpt: string;
  category_id: string;
  tags: string[];
  status: 'draft' | 'published';
  featured_image: string;
  featured_image_alt: string;
  images: Array<{ url: string; alt: string; caption: string }>;
  location_name: string;
  location_country: string;
  location_region: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string[];
}

const AdminEditPost = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [formData, setFormData] = useState<PostFormData>({
    title: '',
    content: '',
    excerpt: '',
    category_id: '',
    tags: [],
    status: 'draft',
    featured_image: '',
    featured_image_alt: '',
    images: [],
    location_name: '',
    location_country: '',
    location_region: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: [],
  });

  const fetchPost = useCallback(async () => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_ENDPOINTS.posts}/admin/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch post');

      const result = await response.json();
      if (result.success) {
        const post = result.data;
        setFormData({
          title: post.title || '',
          content: post.content || '',
          excerpt: post.excerpt || '',
          category_id: post.category_id || '',
          tags: post.tags?.map((tag: any) => tag.id) || [],
          status: post.status || 'draft',
          featured_image: post.featured_image || '',
          featured_image_alt: post.featured_image_alt || '',
          images: post.images || [],
          location_name: post.location_name || '',
          location_country: post.location_country || '',
          location_region: post.location_region || '',
          meta_title: post.meta_title || '',
          meta_description: post.meta_description || '',
          meta_keywords: post.meta_keywords || [],
        });
      }
    } catch (error) {
      console.error('Failed to fetch post:', error);
      alert('Failed to load post');
    } finally {
      setInitialLoading(false);
    }
  }, [id]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.categories}`);
      const result = await response.json();
      if (result.success) {
        setCategories(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.tags}`);
      const result = await response.json();
      if (result.success) {
        setTags(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (id) {
      fetchPost();
      fetchCategories();
      fetchTags();
    }
  }, [id, isAuthenticated, navigate, fetchPost, fetchCategories, fetchTags]);

  const handleAIContentGenerated = (
    content: string,
    type: 'title' | 'content' | 'excerpt' | 'seo'
  ) => {
    switch (type) {
      case 'title':
        setFormData(prev => ({ ...prev, title: content }));
        break;
      case 'content':
        setFormData(prev => ({ ...prev, content: content }));
        break;
      case 'excerpt':
        setFormData(prev => ({ ...prev, excerpt: content }));
        break;
      case 'seo':
        setFormData(prev => ({ ...prev, meta_description: content }));
        break;
    }
  };

  const handleSubmit = async (
    e: React.FormEvent,
    status?: 'draft' | 'published'
  ) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = getAuthToken();
      const submitData = {
        ...formData,
        ...(status && { status }),
        meta_title: formData.meta_title || formData.title,
        meta_description: formData.meta_description || formData.excerpt,
      };

      const response = await fetch(`${API_ENDPOINTS.posts}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });

      if (!response.ok) throw new Error('Failed to update post');

      const result = await response.json();
      if (result.success) {
        navigate('/admin/posts');
      }
    } catch (error) {
      alert('Failed to update post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tagId: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagId)
        ? prev.tags.filter(id => id !== tagId)
        : [...prev.tags, tagId],
    }));
  };

  const handleNewTagCreated = (newTag: Tag) => {
    setTags(prev => [...prev, newTag]);
  };

  const generateExcerpt = () => {
    if (formData.content) {
      const text = formData.content.replace(/<[^>]*>/g, '').substring(0, 160);
      setFormData(prev => ({
        ...prev,
        excerpt: text + (text.length === 160 ? '...' : ''),
      }));
    }
  };

  if (initialLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-64"></div>
          <div className="h-40 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/admin/posts')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-display font-semibold text-gray-900">
              Edit Post
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Make changes to your blog post
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ocean-500"
          >
            <EyeIcon className="h-4 w-4 mr-2" />
            {showPreview ? 'Hide Preview' : 'Preview'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title */}
          <div>
            <label
              htmlFor="title"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Title *
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={e =>
                setFormData(prev => ({ ...prev, title: e.target.value }))
              }
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-ocean-500 focus:border-ocean-500 sm:text-sm"
              placeholder="Enter post title..."
              required
            />
          </div>

          {/* Content Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content *
            </label>

            {/* AI Content Generator */}
            <div className="mb-4">
              <AIContentButtons
                onContentGenerated={content =>
                  setFormData(prev => ({ ...prev, content }))
                }
                postId={id}
                title={formData.title}
                category={
                  categories.find(c => c.id === formData.category_id)?.name
                }
                location={
                  formData.location_country
                    ? `${formData.location_name}, ${formData.location_country}`
                    : formData.location_name
                }
              />
            </div>

            <RichTextEditor
              content={formData.content}
              onChange={content => setFormData(prev => ({ ...prev, content }))}
              placeholder="Start writing your post..."
              postTitle={formData.title}
            />
          </div>

          {/* Excerpt */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label
                htmlFor="excerpt"
                className="block text-sm font-medium text-gray-700"
              >
                Excerpt
              </label>
              <div className="flex space-x-2">
                <AIGenerateButton
                  type="excerpt"
                  onContentGenerated={content =>
                    setFormData(prev => ({ ...prev, excerpt: content }))
                  }
                  title={formData.title}
                  content={formData.content}
                  category={
                    categories.find(c => c.id === formData.category_id)?.name
                  }
                />
                <button
                  type="button"
                  onClick={generateExcerpt}
                  className="text-xs text-ocean-600 hover:text-ocean-700"
                >
                  Generate from content
                </button>
              </div>
            </div>
            <textarea
              id="excerpt"
              rows={3}
              value={formData.excerpt}
              onChange={e =>
                setFormData(prev => ({ ...prev, excerpt: e.target.value }))
              }
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-ocean-500 focus:border-ocean-500 sm:text-sm"
              placeholder="Brief description of the post..."
            />
          </div>

          {/* Location */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label
                htmlFor="location_name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Location
              </label>
              <input
                type="text"
                id="location_name"
                value={formData.location_name}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    location_name: e.target.value,
                  }))
                }
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-ocean-500 focus:border-ocean-500 sm:text-sm"
                placeholder="e.g., Bali"
              />
            </div>
            <div>
              <label
                htmlFor="location_region"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Region
              </label>
              <input
                type="text"
                id="location_region"
                value={formData.location_region}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    location_region: e.target.value,
                  }))
                }
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-ocean-500 focus:border-ocean-500 sm:text-sm"
                placeholder="e.g., Ubud"
              />
            </div>
            <div>
              <label
                htmlFor="location_country"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Country
              </label>
              <input
                type="text"
                id="location_country"
                value={formData.location_country}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    location_country: e.target.value,
                  }))
                }
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-ocean-500 focus:border-ocean-500 sm:text-sm"
                placeholder="e.g., Indonesia"
              />
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Update Actions */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Update</h3>
            <div className="space-y-3">
              <button
                onClick={e => handleSubmit(e)}
                disabled={loading || !formData.title || !formData.content}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ocean-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              {formData.status === 'draft' && (
                <button
                  onClick={e => handleSubmit(e, 'published')}
                  disabled={loading || !formData.title || !formData.content}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-ocean-600 hover:bg-ocean-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ocean-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Publishing...' : 'Publish Now'}
                </button>
              )}
              {formData.status === 'published' && (
                <button
                  onClick={e => handleSubmit(e, 'draft')}
                  disabled={loading}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-yellow-300 shadow-sm text-sm font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Unpublishing...' : 'Unpublish'}
                </button>
              )}
            </div>
          </div>

          {/* Status */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Status</h3>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                formData.status === 'published'
                  ? 'bg-green-100 text-green-800'
                  : formData.status === 'draft'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-gray-100 text-gray-800'
              }`}
            >
              {formData.status}
            </span>
          </div>

          {/* Category */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Category</h3>
            <select
              value={formData.category_id}
              onChange={e =>
                setFormData(prev => ({ ...prev, category_id: e.target.value }))
              }
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-ocean-500 focus:border-ocean-500 sm:text-sm"
            >
              <option value="">Select a category...</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Tags */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Tags</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {tags.map(tag => (
                <label key={tag.id} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.tags.includes(tag.id)}
                    onChange={() => handleTagToggle(tag.id)}
                    className="rounded border-gray-300 text-ocean-600 shadow-sm focus:border-ocean-300 focus:ring focus:ring-ocean-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">{tag.name}</span>
                </label>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100">
              <AITagSuggestions
                title={formData.title}
                content={formData.content}
                category={
                  categories.find(c => c.id === formData.category_id)?.name
                }
                location={formData.location_name}
                existingTags={tags}
                selectedTagIds={formData.tags}
                onTagToggle={handleTagToggle}
                onNewTagCreated={handleNewTagCreated}
              />
            </div>
          </div>

          {/* Featured Image */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-4">
              Featured Image
            </h3>
            <div className="space-y-3">
              <input
                type="url"
                value={formData.featured_image}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    featured_image: e.target.value,
                  }))
                }
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-ocean-500 focus:border-ocean-500 sm:text-sm"
                placeholder="Image URL..."
              />
              <input
                type="text"
                value={formData.featured_image_alt}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    featured_image_alt: e.target.value,
                  }))
                }
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-ocean-500 focus:border-ocean-500 sm:text-sm"
                placeholder="Alt text..."
              />
              {formData.featured_image && (
                <img
                  src={formData.featured_image}
                  alt={formData.featured_image_alt}
                  className="w-full h-32 object-cover rounded-md"
                />
              )}
            </div>
          </div>

          {/* SEO */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-4">SEO</h3>
            <div className="space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">
                    Meta Title
                  </label>
                  <AIGenerateButton
                    type="title"
                    onContentGenerated={content =>
                      setFormData(prev => ({ ...prev, meta_title: content }))
                    }
                    title={formData.title}
                    content={formData.content}
                    category={
                      categories.find(c => c.id === formData.category_id)?.name
                    }
                  />
                </div>
                <input
                  type="text"
                  value={formData.meta_title}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      meta_title: e.target.value,
                    }))
                  }
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-ocean-500 focus:border-ocean-500 sm:text-sm"
                  placeholder="Meta title (defaults to post title)"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">
                    Meta Description
                  </label>
                  <AIGenerateButton
                    type="seo"
                    onContentGenerated={content =>
                      setFormData(prev => ({
                        ...prev,
                        meta_description: content,
                      }))
                    }
                    title={formData.title}
                    content={formData.content}
                    category={
                      categories.find(c => c.id === formData.category_id)?.name
                    }
                  />
                </div>
                <textarea
                  rows={2}
                  value={formData.meta_description}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      meta_description: e.target.value,
                    }))
                  }
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-ocean-500 focus:border-ocean-500 sm:text-sm"
                  placeholder="Meta description (defaults to excerpt)"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">
                    Meta Keywords
                  </label>
                  <AIKeywordSuggestButton
                    title={formData.title}
                    content={formData.content}
                    category={
                      categories.find(c => c.id === formData.category_id)?.name
                    }
                    onKeywordsGenerated={keywords =>
                      setFormData(prev => ({
                        ...prev,
                        meta_keywords: keywords,
                      }))
                    }
                  />
                </div>
                {formData.meta_keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {formData.meta_keywords.map((keyword, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full"
                      >
                        {keyword}
                        <button
                          type="button"
                          onClick={() =>
                            setFormData(prev => ({
                              ...prev,
                              meta_keywords: prev.meta_keywords.filter(
                                (_, i) => i !== index
                              ),
                            }))
                          }
                          className="ml-1 text-gray-400 hover:text-red-500"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Type a keyword and press Enter..."
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-ocean-500 focus:border-ocean-500 sm:text-sm"
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const value = (e.target as HTMLInputElement).value.trim();
                      if (value && !formData.meta_keywords.includes(value)) {
                        setFormData(prev => ({
                          ...prev,
                          meta_keywords: [...prev.meta_keywords, value],
                        }));
                        (e.target as HTMLInputElement).value = '';
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Writing Assistant */}
      <AIWritingAssistant
        onContentGenerated={handleAIContentGenerated}
        postData={{
          title: formData.title,
          category: categories.find(c => c.id === formData.category_id)?.name,
          location: formData.location_country
            ? `${formData.location_name}, ${formData.location_country}`
            : formData.location_name,
          keywords: formData.meta_keywords,
        }}
      />

      {/* Post Preview */}
      {showPreview && (
        <PostPreview
          title={formData.title}
          content={formData.content}
          excerpt={formData.excerpt}
          featured_image={formData.featured_image}
          featured_image_alt={formData.featured_image_alt}
          images={formData.images}
          category_name={
            categories.find(c => c.id === formData.category_id)?.name
          }
          tag_names={tags
            .filter(t => formData.tags.includes(t.id))
            .map(t => t.name)}
          location_name={formData.location_name}
          location_country={formData.location_country}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
};

export default AdminEditPost;
